import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPin, Maximize2, Minimize2, Copy, Check, ExternalLink, Navigation } from 'lucide-react';
import { cn } from '@/frontend/lib/utils';
import {
  NOMINATIM_BASE,
  TILE_URL,
  TILE_ATTRIBUTION,
  pinIcon,
  usePoiLayers,
  MapController,
  PoiLayerToggles,
  PoiMarkers,
} from '@/frontend/components/map/mapShared';

interface ResidenceMapProps {
  address: string;
  residenceName: string;
}

type GeocodingStatus = 'idle' | 'loading' | 'success' | 'error';

export function ResidenceMap({ address, residenceName }: ResidenceMapProps) {
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [status, setStatus] = useState<GeocodingStatus>('idle');
  const [interactive, setInteractive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { activeLayers, pois, poisLoading, toggleLayer } = usePoiLayers(coords);
  const mapWrapperRef = useRef<HTMLDivElement>(null);

  // Geocoding with retry
  useEffect(() => {
    if (!address) return;
    setStatus('loading');
    const controller = new AbortController();
    let retries = 0;
    const maxRetries = 2;

    const geocode = () => {
      fetch(
        `${NOMINATIM_BASE}/search?q=${encodeURIComponent(address)}&format=json&limit=1`,
        { signal: controller.signal }
      )
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (data && data.length > 0) {
            setCoords([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
            setStatus('success');
          } else {
            setStatus('error');
          }
        })
        .catch(err => {
          if (err.name === 'AbortError') return;
          if (retries < maxRetries) {
            retries++;
            setTimeout(geocode, 1000 * retries);
          } else {
            setStatus('error');
          }
        });
    };

    geocode();
    return () => controller.abort();
  }, [address]);

  // Fullscreen state sync
  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mapWrapperRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const openDirections = () => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`,
      '_blank', 'noopener,noreferrer'
    );
  };

  const openInMaps = () => {
    const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
    const url = isApple
      ? `https://maps.apple.com/?q=${encodeURIComponent(address)}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openStreetView = () => {
    if (!coords) return;
    window.open(
      `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${coords[0]},${coords[1]}`,
      '_blank', 'noopener,noreferrer'
    );
  };

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="h-80 md:h-[450px] bg-muted rounded-xl flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <div className="w-6 h-6 border-2 border-muted-foreground border-t-secondary rounded-full animate-spin" />
          <span className="text-sm">Loading map...</span>
        </div>
      </div>
    );
  }

  if (status === 'error' || !coords) {
    return (
      <div className="h-80 md:h-[450px] bg-muted rounded-xl flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-muted-foreground text-center px-4">
          <MapPin className="w-6 h-6" />
          <span className="text-sm">Map could not be loaded for this address.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Map */}
      <div
        ref={mapWrapperRef}
        className={cn(
          'rounded-xl overflow-hidden border border-border isolate relative',
          !isFullscreen && 'h-80 md:h-[450px]'
        )}
      >
        {/* POI layer toggles — inside map so they survive fullscreen */}
        <PoiLayerToggles activeLayers={activeLayers} poisLoading={poisLoading} toggleLayer={toggleLayer} />

        {/* Fullscreen button */}
        <button
          onClick={toggleFullscreen}
          className="absolute top-2 right-2 z-[900] bg-white/90 hover:bg-white border border-border rounded-md p-1.5 shadow-sm transition-colors"
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen
            ? <Minimize2 className="w-4 h-4 text-foreground" />
            : <Maximize2 className="w-4 h-4 text-foreground" />}
        </button>

        {/* Click-to-interact overlay */}
        {!interactive && (
          <div
            className="absolute inset-0 z-[800] flex items-center justify-center cursor-pointer"
            onClick={() => setInteractive(true)}
          >
            <div className="bg-white/90 backdrop-blur-sm border border-border rounded-lg px-4 py-2 text-sm font-medium shadow-sm flex items-center gap-2 text-foreground">
              <Navigation className="w-4 h-4" />
              Click to interact
            </div>
          </div>
        )}

        <MapContainer
          center={coords}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            attribution={TILE_ATTRIBUTION}
            url={TILE_URL}
          />

          <Marker position={coords} icon={pinIcon}>
            <Popup>
              <div style={{ minWidth: '160px' }}>
                <p style={{ fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>{residenceName}</p>
                <p style={{ fontSize: '12px', color: '#6b7280' }}>{address}</p>
                <button
                  onClick={openDirections}
                  style={{ fontSize: '12px', color: '#2563eb', marginTop: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Get directions →
                </button>
              </div>
            </Popup>
          </Marker>

          <PoiMarkers activeLayers={activeLayers} pois={pois} />

          <MapController interactive={interactive} />
        </MapContainer>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={openDirections}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-background border border-border hover:bg-muted transition-colors text-foreground"
        >
          <Navigation className="w-3.5 h-3.5" />
          Get Directions
        </button>
        <button
          onClick={openInMaps}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-background border border-border hover:bg-muted transition-colors text-foreground"
        >
          <MapPin className="w-3.5 h-3.5" />
          Open in Maps
        </button>
        <button
          onClick={copyAddress}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-background border border-border hover:bg-muted transition-colors text-foreground"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied!' : 'Copy Address'}
        </button>
        <button
          onClick={openStreetView}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-background border border-border hover:bg-muted transition-colors text-foreground"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Street View
        </button>
      </div>
    </div>
  );
}
